/**
 * 读图遭遇失败/429 后的退避：按「用户 + 域」分槽，避免整页全局冻结。
 * - view-proxy：HMAC `/api/storage/images/view`（后端 IP 限流面）
 * - oss:&lt;host&gt;：OSS 预签名直链
 * - host:&lt;host&gt;：其它 http(s)
 */

import { isOssDirectDisplayUrl, isSignedViewPath } from '@/utils/imageDisplayAccess';

/** @type {Map<string, { until: number, streak: number, timer: number|null }>} */
const scopes = new Map();

let userScope = 'anon';
let resumeTimer = null;

/**
 * 由 AuthProvider 同步当前用户，使多账号同浏览器互不影响冷却槽。
 * @param {string|number|null|undefined} userId
 */
export function setViewLoadRateLimitUserScope(userId) {
  const next = userId == null || userId === '' ? 'anon' : String(userId);
  if (next === userScope) return;
  userScope = next;
}

export function getViewLoadRateLimitUserScope() {
  return userScope;
}

/**
 * @param {string} [url]
 * @returns {string} scope key（不含 user 前缀时亦可；完整 key 见 fullScopeKey）
 */
export function resolveViewLoadDomainKey(url) {
  const s = String(url || '').trim();
  if (!s) return 'unknown';
  if (isSignedViewPath(s)) return 'view-proxy';
  if (isOssDirectDisplayUrl(s)) {
    try {
      const host = new URL(s, typeof window !== 'undefined' ? window.location.origin : 'http://local').hostname;
      return `oss:${host || 'unknown'}`;
    } catch {
      return 'oss:unknown';
    }
  }
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      return `host:${new URL(s).hostname || 'unknown'}`;
    } catch {
      return 'host:unknown';
    }
  }
  return 'local';
}

function fullScopeKey(domainKey) {
  return `${userScope}|${domainKey}`;
}

function getScopeState(scopeKey) {
  let state = scopes.get(scopeKey);
  if (!state) {
    state = { until: 0, streak: 0, timer: null };
    scopes.set(scopeKey, state);
  }
  return state;
}

export function isViewLoadCooldownActive(url) {
  if (url == null || url === '') {
    // 无 URL：仅当「当前用户」下仍有活跃冷却时为 true（兼容旧调用，不再整页无差别冻结）
    const prefix = `${userScope}|`;
    const now = Date.now();
    for (const [key, state] of scopes) {
      if (key.startsWith(prefix) && now < state.until) return true;
    }
    return false;
  }
  const key = fullScopeKey(resolveViewLoadDomainKey(url));
  const state = scopes.get(key);
  return Boolean(state && Date.now() < state.until);
}

function scheduleQueueResume(delayMs) {
  if (typeof window === 'undefined') return;
  if (resumeTimer != null) {
    window.clearTimeout(resumeTimer);
    resumeTimer = null;
  }
  resumeTimer = window.setTimeout(() => {
    resumeTimer = null;
    import('@/utils/managedImageViewLoadQueue')
      .then((mod) => {
        mod.resumeManagedViewLoadQueue?.();
      })
      .catch(() => {});
  }, Math.max(0, delayMs) + 16);
}

/**
 * @param {string} [url] 失败资源 URL；缺省时记到 unknown（应尽量传入）
 */
export function noteViewLoadFailure(url) {
  const domain = resolveViewLoadDomainKey(url);
  const key = fullScopeKey(domain);
  const state = getScopeState(key);
  state.streak = Math.min(state.streak + 1, 6);
  // view-proxy 用指数退避（对齐后端 429）；其它域更短，避免 OSS 过期/偶发错拖死列表
  const base = domain === 'view-proxy' ? 800 : 400;
  const cap = domain === 'view-proxy' ? 12_000 : 4_000;
  const delayMs = Math.min(cap, base * (2 ** (state.streak - 1)));
  state.until = Math.max(state.until, Date.now() + delayMs);
  scheduleQueueResume(state.until - Date.now());
}

export function noteViewLoadSuccess(url) {
  if (url == null || url === '') {
    // 成功且无 URL：不整表清零，避免误伤其它域冷却
    return;
  }
  const key = fullScopeKey(resolveViewLoadDomainKey(url));
  const state = scopes.get(key);
  if (state) {
    state.streak = 0;
  }
}

export function waitForViewLoadCooldown(url) {
  if (typeof window === 'undefined') return Promise.resolve();
  let remain;
  if (url == null || url === '') {
    const prefix = `${userScope}|`;
    const now = Date.now();
    remain = 0;
    for (const [key, state] of scopes) {
      if (key.startsWith(prefix)) {
        remain = Math.max(remain, state.until - now);
      }
    }
  } else {
    const key = fullScopeKey(resolveViewLoadDomainKey(url));
    const state = scopes.get(key);
    remain = state ? state.until - Date.now() : 0;
  }
  if (remain <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve();
      scheduleQueueResume(0);
    }, remain);
  });
}

/** @internal 测试用 */
export function __resetViewLoadRateLimitGuardForTests() {
  scopes.clear();
  userScope = 'anon';
  if (typeof window !== 'undefined' && resumeTimer != null) {
    window.clearTimeout(resumeTimer);
  }
  resumeTimer = null;
}
